import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { KPI } from '@/components/ui/kpi';
import { Eyebrow } from '@/components/ui/eyebrow';
import { Plus, ScrollText, Receipt, TrendingUp, Search } from 'lucide-react';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { useAuth } from '@/lib/auth';
import {
  useDocumentosFiscais,
  useDocumentosFiscaisAggregate,
  type TipoDocumento,
} from '@/lib/hooks/documentos-fiscais';
import { DocumentoFiscalDrawer } from '@/features/documentos-fiscais/DocumentoFiscalDrawer';
import { DocumentosFiscaisTable } from '@/features/documentos-fiscais/DocumentosFiscaisTable';

export const Route = createFileRoute('/_app/lancamentos/documentos-fiscais')({
  component: DocumentosFiscaisPage,
});

function currentMes(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const PAGE_SIZE = 25;

function DocumentosFiscaisPage() {
  const { user } = useAuth();
  const workspaceId = user?.effectiveWorkspaceId ?? null;

  const [mes, setMes] = useState(currentMes());
  const [tipo, setTipo] = useState<TipoDocumento | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const filters = useMemo(() => {
    const dataInicio = mes ? `${mes}-01` : undefined;
    const dataFim = mes ? lastDayOf(mes) : undefined;
    return {
      tipo: tipo === 'ALL' ? undefined : tipo,
      search: search.trim() || undefined,
      dataInicio,
      dataFim,
      limit: PAGE_SIZE,
      offset,
    };
  }, [mes, tipo, search, offset]);

  const listQ = useDocumentosFiscais(workspaceId, filters);
  const aggQ = useDocumentosFiscaisAggregate(workspaceId);

  const items = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;
  const agg = aggQ.data;

  const totalValor = Number(agg?.total ?? '0');
  const totalCount = agg?.count ?? 0;
  const ticketMedio = totalCount > 0 ? totalValor / totalCount : 0;

  const mesLabel = mes
    ? new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(
        new Date(`${mes}-01T12:00:00`),
      )
    : '';

  if (!workspaceId) {
    return (
      <Card variant="default" className="p-8 text-center">
        <p className="text-sm text-fg-muted">
          Voce esta logado como administrador da plataforma. Selecione um workspace para gerenciar
          documentos fiscais.
        </p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Eyebrow>Lancamento · Mensal</Eyebrow>
          <h1 className="font-display text-[34px] md:text-[42px] font-bold tracking-[-0.03em] text-fg leading-[1.0] mt-1">
            Documentos fiscais{' '}
            <span className="text-fg-muted font-normal">· {mesLabel}</span>
          </h1>
          <p className="text-sm text-fg-muted mt-2">
            NF-e, NFS-e e NFA-e que respaldam as despesas
          </p>
        </div>
        <Button variant="primary" size="lg" onClick={() => setDrawerOpen(true)}>
          <Plus className="h-4 w-4" strokeWidth={1.8} />
          Nova nota fiscal
        </Button>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KPI
          tone="mint"
          label="Total NF no mes"
          value={String(totalCount)}
          delta={`${totalCount === 1 ? 'documento' : 'documentos'}`}
          icon={ScrollText}
        />
        <KPI
          label="Valor total"
          value={fmtCurrency(agg?.total ?? '0')}
          delta={`${totalCount} ${totalCount === 1 ? 'nota' : 'notas'}`}
          icon={Receipt}
        />
        <KPI
          tone="ink"
          label="Ticket medio"
          value={totalCount > 0 ? fmtCurrency(ticketMedio.toFixed(2)) : '—'}
          delta={totalCount > 0 ? `media de ${totalCount} docs` : 'sem dados'}
          icon={TrendingUp}
        />
      </section>

      <Card variant="default" className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-fg-muted"
              strokeWidth={1.8}
            />
            <Input
              placeholder="Buscar por emitente…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOffset(0);
              }}
              className="pl-9"
            />
          </div>
          <Input
            type="month"
            value={mes}
            onChange={(e) => {
              setMes(e.target.value);
              setOffset(0);
            }}
            className="w-[160px]"
          />
          <Select
            value={tipo}
            onValueChange={(v) => {
              setTipo(v as TipoDocumento | 'ALL');
              setOffset(0);
            }}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos os tipos</SelectItem>
              <SelectItem value="NFSE">NFS-e</SelectItem>
              <SelectItem value="NFE">NF-e</SelectItem>
              <SelectItem value="NFAE">NFA-e</SelectItem>
              <SelectItem value="OUTRO">Outro</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <DocumentosFiscaisTable
        workspaceId={workspaceId}
        data={items}
        total={total}
        limit={PAGE_SIZE}
        offset={offset}
        loading={listQ.isLoading}
        onPageChange={setOffset}
      />

      <DocumentoFiscalDrawer
        workspaceId={workspaceId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </div>
  );
}

function fmtCurrency(value: string | number): string {
  const n = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(n)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);
}

function lastDayOf(mes: string): string {
  const parts = mes.split('-');
  const year = Number(parts[0] ?? '2000');
  const month = Number(parts[1] ?? '1');
  const last = new Date(year, month, 0);
  return `${year}-${String(month).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}`;
}
